import { createLogger } from '@app/utils/loggerCreator'

import { analyticsEvent } from '@app/packages/analytics/declaration'
import { postFirebaseFcmSubscribe } from '@app/packages/api/v2/actions/firebase'
import { AsyncTask } from '@app/packages/asyncTask/asyncTask'
import { dispose, withDisposableStack, withDispose } from '@app/packages/disposable/disposable'
import { spawn } from '@app/packages/disposable/spawn'
import { ComputedValue } from '@app/packages/reactives/ComputedValue'
import { ReactiveValue } from '@app/packages/reactives/ReactiveValue'
import type { TrackableValue } from '@app/packages/reactives/TrackableValue'

import { getAnalyticsRouter } from '@app/store/actions/analytics'
import { callV2Api } from '@app/store/actions/api'
import { getFirebaseMessagingManager } from '@app/store/actions/contexts/firebaseMessagingManager'
import { configSelector } from '@app/store/slices/config'
import { profileUserSelector } from '@app/store/slices/profile'
import type { Store } from '@app/store/store'

export type PushNotificationsManagerInterface = {
  readonly supported: TrackableValue<boolean>
  readonly subscribed: TrackableValue<boolean>
  readonly blocked: TrackableValue<boolean>
  readonly pending: TrackableValue<boolean>
  readonly token: TrackableValue<string | null>

  subscribe(): Promise<void>
  unsubscribe(): Promise<void>
}

export class PushNotificationsManager implements PushNotificationsManagerInterface {
  readonly supported: TrackableValue<boolean>
  readonly subscribed: TrackableValue<boolean>
  readonly blocked: TrackableValue<boolean>
  readonly pending = new ReactiveValue(false)
  readonly token = new ReactiveValue<string | null>(null)

  private userId: TrackableValue<string | null>
  private blockedBySystem = new ReactiveValue(false)
  private blockedByFirebase = new ReactiveValue(false)
  private store: Store
  private stack = new DisposableStack()
  private isApp: TrackableValue<boolean>
  private lock: SubscribeTask | UnsubscribeTask | null = null
  private logger = createLogger('PushNotificationsManager', 'teal')

  constructor(store: Store) {
    this.logger.log('Initializing')
    this.stack.defer(() => {
      this.logger.log('Disposing')
    })

    this.store = store
    this.userId = this.stack.use(this.trackableFromSelector((state: unknown) => profileUserSelector(state)?.id || null))
    this.isApp = this.stack.use(this.trackableFromSelector(state => configSelector(state).isApp))
    const isBrowserSupported = new ReactiveValue(this.isBrowserSupported())

    let storageDisposable: Disposable | null = null
    this.stack.defer(() => {
      if (storageDisposable) {
        this.logger.log('Disposing storage listener')
        dispose(storageDisposable)
        storageDisposable = null
      }
    })

    this.stack.use(
      this.userId.sync(userId => {
        this.logger.log('User ID sync', userId)
        this.token.update(() => (userId ? new Storage(userId).get() : null))

        if (storageDisposable) {
          dispose(storageDisposable)
          storageDisposable = null
        }
        if (!userId) return
        storageDisposable = new Storage(userId).listen(token => {
          this.token.update(() => token)
        })
      })
    )

    this.stack.use(
      spawn(async stack => {
        const permissions = await this.getNavigatorPermissions()
        if (!permissions) return

        const handler = () => {
          this.logger.log('Notification permissions changed', permissions.state)
          this.store.dispatch(getAnalyticsRouter()).send(notificationPermissionsChangedEvent(permissions.state))

          if (permissions.state !== 'granted') {
            const userId = this.userId.value
            if (userId) new Storage(userId).unset()
          }

          this.blockedBySystem.update(() => permissions.state === 'denied')
        }

        if (permissions.addEventListener) {
          permissions.addEventListener('change', handler)
          stack.defer(() => {
            permissions.removeEventListener('change', handler)
          })
        } else {
          const or = permissions.onchange
          permissions.onchange = ev => {
            or?.call(permissions, ev)
            handler()
          }
          stack.defer(() => {
            permissions.onchange = or
          })
        }
      })
    )

    const canLoad = this.stack.use(
      new ComputedValue([this.isApp, isBrowserSupported], (isApp, browserSupported) => {
        return !isApp && browserSupported
      })
    )

    const isFirebaseSupported = new ReactiveValue<boolean | null>(null)

    this.stack.use(
      spawn(async stack => {
        const onLoad = async () => {
          const firebaseMessagingManager = await this.store.dispatch(getFirebaseMessagingManager())
          const val = !!(await firebaseMessagingManager?.isSupported())
          isFirebaseSupported.update(() => val)
        }

        if (canLoad.value) {
          spawn(onLoad)
        } else {
          let canLoadDispose: Disposable | null

          canLoadDispose = canLoad.sync(async canLoad => {
            if (canLoad && canLoadDispose) {
              spawn(onLoad)
              dispose(canLoadDispose)
              canLoadDispose = null
            }
          })

          stack.defer(() => {
            if (canLoadDispose) {
              dispose(canLoadDispose)
              canLoadDispose = null
            }
          })
        }
      })
    )

    const supported = this.stack.use(
      new ComputedValue([this.isApp, isBrowserSupported, isFirebaseSupported], (isApp, browserSupported, firebaseSupported) => {
        return !isApp && browserSupported && !!firebaseSupported
      })
    )
    this.supported = supported

    this.subscribed = this.stack.use(
      new ComputedValue([this.token, this.blockedByFirebase, this.supported], (token, blocked, supported) => !!token && !blocked && supported)
    )

    this.blockedBySystem.update(() => this.isBlocked())

    document.addEventListener(
      'visibilitychange',
      this.stack.adopt(
        () => {
          if (document.visibilityState === 'visible') {
            this.blockedBySystem.update(() => this.isBlocked())
          }
        },
        handler => document.removeEventListener('visibilitychange', handler)
      )
    )

    const blocked = this.stack.use(new ComputedValue([this.blockedBySystem, this.blockedByFirebase], (bySystem, byFirebase) => bySystem || byFirebase))
    this.blocked = blocked

    this.stack.defer(() => {
      this.lock?.cancel()
    })

    const stateComputed = this.stack.use(
      new ComputedValue(
        [
          this.supported,
          isBrowserSupported,
          isFirebaseSupported,
          this.subscribed,
          this.blocked,
          this.pending,
          this.token,
          this.userId,
          this.blockedBySystem,
          this.blockedByFirebase,
        ],
        (supported, browserSupported, firebaseSupported, subscribed, blocked, pending, token, userId, blockedBySystem, blockedByFirebase) => {
          return { supported, browserSupported, firebaseSupported, subscribed, blocked, pending, token, userId, blockedBySystem, blockedByFirebase }
        }
      )
    )

    this.stack.use(
      stateComputed.sync(state => {
        this.logger.log('State changed', state)
      })
    )
  }

  [Symbol.dispose]() {
    this.stack.dispose()
  }

  async subscribe() {
    using stack = new DisposableStack()
    if (this.lock) {
      if (this.lock instanceof SubscribeTask) {
        return await this.lock.value
      }
      this.lock.cancel()
    }

    this.pending.update(() => true)
    stack.defer(() => {
      this.pending.update(() => false)
    })

    const task = new SubscribeTask(async stack => {
      const ab = stack.adopt(new AbortController(), ab => ab.abort())

      const firebaseMessagingManager = await this.store.dispatch(getFirebaseMessagingManager())
      ab.signal.throwIfAborted()
      if (!firebaseMessagingManager) throw new Error('Firebase Messaging is not available')
      const currentToken = await firebaseMessagingManager.subscribe()
      ab.signal.throwIfAborted()
      await this.store.dispatch(callV2Api(postFirebaseFcmSubscribe(currentToken)))
      ab.signal.throwIfAborted()
      this.setToken(currentToken)
      this.store.dispatch(getAnalyticsRouter()).send(webPushesChangedEvent(true))
    })

    this.lock = task
    stack.defer(() => {
      this.lock = null
    })

    return await task.value
  }

  async unsubscribe() {
    using stack = new DisposableStack()
    if (this.lock) {
      if (this.lock instanceof UnsubscribeTask) {
        return await this.lock.value
      }
      this.lock.cancel()
    }

    this.pending.update(() => true)
    stack.defer(() => {
      this.pending.update(() => false)
    })

    const task = new UnsubscribeTask(async stack => {
      const ab = stack.adopt(new AbortController(), ab => ab.abort())

      const firebaseMessageingManager = await this.store.dispatch(getFirebaseMessagingManager())
      ab.signal.throwIfAborted()
      if (!firebaseMessageingManager) throw new Error('Firebase Messaging is not available')
      await firebaseMessageingManager.unsubscribe()
      this.unsetToken()

      this.store.dispatch(getAnalyticsRouter()).send(webPushesChangedEvent(false))
    })

    this.lock = task
    stack.defer(() => {
      this.lock = null
    })

    return await task.value
  }

  private trackableFromSelector<T>(selector: (state: any) => T) {
    const initial = selector(this.store.getState())
    const reactive = new ReactiveValue<T>(initial)
    const unsub = this.store.subscribe(() => {
      const state = this.store.getState()
      reactive.update(() => selector(state))
    })
    return withDispose(reactive, () => {
      unsub()
    })
  }

  private setToken(token: string) {
    const state = this.store.getState()
    const userId = profileUserSelector(state)?.id
    if (!userId) throw new Error('No user id')

    new Storage(userId).set(token)
    this.token.update(() => token)
  }

  private unsetToken() {
    const state = this.store.getState()
    const userId = profileUserSelector(state)?.id
    if (!userId) throw new Error('No user id')

    new Storage(userId).unset()
    this.token.update(() => null)
  }

  private isBlocked() {
    try {
      return window.Notification.permission === 'denied'
    } catch {
      return true
    }
  }

  private isBrowserSupported() {
    return (
      typeof window !== 'undefined' &&
      'serviceWorker' in navigator &&
      !!navigator.serviceWorker &&
      !!window.ServiceWorkerRegistration &&
      'showNotification' in window.ServiceWorkerRegistration.prototype &&
      'PushManager' in window &&
      !!window.PushManager &&
      'permissions' in navigator &&
      !!navigator.permissions &&
      'query' in navigator.permissions
    )
  }

  private getNavigatorPermissions() {
    try {
      return navigator.permissions.query({ name: 'notifications' })
    } catch {
      return null
    }
  }
}

class Storage {
  private base = 'kidsout__fcm_token:'
  private id: string

  constructor(id: string) {
    this.id = id
  }

  get key() {
    return this.base + this.id
  }

  get() {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return null
      const val = window.localStorage.getItem(this.key)
      if (typeof val !== 'string') return null
      return val
    } catch {
      return null
    }
  }

  unset() {
    try {
      window.localStorage.removeItem(this.key)
    } catch {}
  }

  set(value: string) {
    try {
      window.localStorage.setItem(this.key, value)
    } catch {}
  }

  listen(callback: (value: string | null) => void) {
    return withDisposableStack(stack => {
      if (!window.localStorage) throw new Error('No localStorage in this environment')
      window.addEventListener(
        'storage',
        stack.adopt(
          e => {
            if (e.key !== this.key) return
            callback(this.get())
          },
          h => window.removeEventListener('storage', h)
        )
      )
    })
  }
}

class SubscribeTask extends AsyncTask<void> {}
class UnsubscribeTask extends AsyncTask<void> {}

/** `setGAYS()` was google + yandex + amplitude + sentry, and never trackings — hence the four. */
const notificationPermissionsChangedEvent = (state: PermissionState) =>
  analyticsEvent(() => ({
    // The browser's notification permission changed. Not a press: this follows the browser, not a control.
    type: 'change_notifications_permissions_state',
    data: { state },
  })).set('google', 'yandex', 'amplitude', 'sentry')

const webPushesChangedEvent = (state: boolean) =>
  analyticsEvent(() => ({
    // Web pushes were turned on or off, once the subscription had actually changed.
    type: 'receive_web_pushes_changed',
    data: { state },
  })).set('google', 'yandex', 'amplitude', 'sentry')
